<template>
  <section class="pt-details">
    <pu-campsite-link
      v-if="$isDesktop"
      :hierarchy-path="details.path"
      :slug="details.slug"
      hash="pitch-types"
      class="edit-details desktop"
    >
      <booking-edit-icon />
      {{ $t('Edit') }}
    </pu-campsite-link>
    <pu-campsite-availability-link
      v-else
      :hierarchy-path="details.path"
      :slug="details.slug"
      class="edit-details"
    >
      <booking-edit-icon />
      {{ $t('Edit') }}
    </pu-campsite-availability-link>

    <pu-category-icons :categories="details.categories" />
    <p class="pt-name">
      {{ details.pitchTypeName }}
    </p>

    <div v-if="quote" class="number-available">
      <span v-if="isManyPitchesToSell">{{ $t('10+ available') }}</span>
      <span v-else>
        {{
          $t('Only {pitchesToSell} left on our site', {
            pitchesToSell: quote.pitchesToSell,
          })
        }}
      </span>
    </div>

    <pitch-type-dimensions :pitch-type="details" />

    <booking-disallowed-subcategories
      :disallowed-subcategories="details.disallowedSubcategories"
    />

    <unit-per-pitch-short-version
      v-if="details.isByo"
      :one-unit-per-pitch="details.oneUnitPerPitch"
      :allowed-subcategories="details.allowedSubcategories"
      class="booking-unit-per-pitch"
    />

    <table class="dates-and-party">
      <tbody>
        <tr>
          <td>
            <time class="arrive">
              <span class="title">
                <img
                  class="section-icon"
                  src="@/assets/images/calendar-star-blue.svg"
                  width="16"
                  height="16"
                  alt=""
                />
                {{ $t('Arrive') }}
              </span>
              <pu-utc-date
                tag="span"
                class="date"
                :date="params.stayRange.start"
                :options="{ weekday: 'long', day: 'numeric', month: 'short' }"
              />
              <pu-utc-date
                tag="span"
                class="year"
                :date="params.stayRange.start"
                options="year"
              />
            </time>
          </td>
          <td>
            <time>
              <span class="title">
                <img
                  class="section-icon"
                  src="@/assets/images/calendar-star-blue.svg"
                  width="16"
                  height="16"
                  alt=""
                />
                {{ $t('Depart') }}
              </span>
              <pu-utc-date
                tag="span"
                class="date"
                :date="params.stayRange.end"
                :options="{ weekday: 'long', day: 'numeric', month: 'short' }"
              />
              <pu-utc-date
                tag="span"
                class="year"
                :date="params.stayRange.end"
                options="year"
              />
            </time>
          </td>
        </tr>
        <tr>
          <td>
            <div class="title">
              <booking-party
                v-if="quote"
                :adults="quote.convertedParty.adults"
                :converted-children-ages="quote.convertedParty.childrenAges"
                :children-ages="params.party.childAges"
                :infants="quote.convertedParty.infants"
                :infants-ages="quote.convertedParty.infantsAges"
                :min-adult-age="minAdultAge"
              />
            </div>
          </td>
          <td>
            <div class="title">
              <img
                class="section-icon"
                src="@/assets/images/moon.svg"
                width="16"
                height="16"
                alt=""
              />
              <translate :tt="{ nights }"> nights {nights} </translate>
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </section>
</template>

<script lang="ts">
import { DateRange } from '~/utility/date/DateRange'
import { Component, Prop, Vue, toNative } from '~/utility/pu-class-decorator'
import { BookingInfo } from '../data/schema'
import { Quote, QuoteRequestParams } from '../quote/schema'

@Component
class BookingStayDetails extends Vue {
  @Prop()
  details: BookingInfo

  @Prop()
  params: QuoteRequestParams

  @Prop()
  quote: Quote

  get nights() {
    const { start, end } = this.params.stayRange
    return new DateRange(start, end).nights
  }

  get children(): number {
    return this.params.party.childAges.length
  }

  get isManyPitchesToSell() {
    return this.quote.pitchesToSell > 10
  }

  get minAdultAge(): number {
    return this.details.maxChildAge + 1
  }
}

export default toNative(BookingStayDetails)
</script>

<style scoped>
.pt-details {
  background-color: #eff6ff;
  font-size: var(--body);
  position: relative;
  padding: 1.4em;
  min-height: 17em;
}

.edit-details {
  position: absolute;
  top: 0;
  right: 0;
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  text-decoration: none;
  padding: 0.2em 0.4em;
  margin: 1em;
  border: 1px solid #92bad4;
  border-radius: 0.4em;
}

.edit-details img {
  width: 1em;
  margin-right: 0.2em;
}

.pt-details .category-icons {
  font-size: 1.3em;
  display: inline-flex;
}

.number-available {
  font-size: var(--body);
  color: #679dbf;
  margin-bottom: 1em;
}

.pt-name {
  font-weight: bold;
  margin: 0.5em 0 0;
}

.pt-details :deep(.unit-per-pitch) {
  font-size: inherit;
  margin-top: 0.25em;
}

.pt-details :deep(.one-unit-per-pitch p) {
  font-size: inherit;
}

.dates-and-party {
  margin: 1em 0 0 0;
  border-top: 1px solid #d1dbe1;
  width: 100%;
}

.dates-and-party tr {
  border: none;
}

.dates-and-party tr td:first-of-type {
  padding-right: 1em;
}

.dates-and-party td {
  padding: 1em 0 0 0;
  min-width: 30%;
}

.section-icon {
  height: 1em;
  margin: 0.15em 0.35em 0 0;
  width: auto;
}

.title {
  align-items: flex-start;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
}

span.title {
  color: var(--colour-form-blue);
  padding: 0 0 0.25em 0;
}

.date {
  font-weight: bold;
  white-space: nowrap;
}

time {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.edit-details.desktop:hover,
.edit-details.desktop:focus-visible {
  background: #bdced7;
  color: var(--colour-dark-blue);
  border-color: #bdced7 !important;
}
</style>
